import {
  ExpandLess,
  ExpandMore,
  MoreVert,
  Description,
  Person,
  Schedule,
  CalendarToday,
  ArrowBack,
  Download,
  ContentCopy,
  Warning,
  Search,
  AttachFile,
  FilterList,
} from "@mui/icons-material";
import React, { useState, useEffect, useMemo } from "react";
import "./appointmentDetailView.css"; // Import your CSS styles
import { useSelector } from "react-redux";
import ChatModal from "../chatModal";
import { supabase } from "../../../supabaseClient";
import { canJoinMeetingNow, formatMeetingWindowLocal, formatCountdown, formatMeetingDetails } from '../../../helpers/stringHelper';

const AppointmentDetailView = ({
  appointment,
  onBack,
  onAccept,
  onReject,
  appointments = [],
  onAppointmentUpdate, // New prop to handle appointment updates from parent
}) => {
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(
    appointment.id
  );
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [intakeStatus, setIntakeStatus] = useState({}); // Track intake status for appointments
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [joinCountdown, setJoinCountdown] = useState(0);
  const [joinWindowText, setJoinWindowText] = useState("");

  function openChat(user) {
    setChatUser(user);
    setChatOpen(true);
  }

  function closeChat() {
    setChatOpen(false);
    setChatUser(null);
  }

  // Set up Supabase real-time subscription for intake notifications
  useEffect(() => {
    // Subscribe to the 'intake_submitted' channel
    const channel = supabase.channel("intake_notifications");

    channel
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "appointments", // Adjust table name as needed
        },
        (payload) => {
          console.log("Appointment updated:", payload);
          // Handle appointment updates if needed
          if (onAppointmentUpdate) {
            onAppointmentUpdate(payload.new);
          }
        }
      )
      .subscribe();

    // Also listen for custom notifications using pg_notify
    const notificationChannel = supabase.channel("custom_notifications");

    // Note: Direct pg_notify listening might require a different approach
    // You might need to poll for changes or use Supabase's built-in real-time features

    return () => {
      channel.unsubscribe();
      notificationChannel.unsubscribe();
    };
  }, [onAppointmentUpdate]);

  // Alternative approach: Poll for intake status updates
  useEffect(() => {
    const checkIntakeStatus = async () => {
      try {
        const appointmentIds = appointments.map((apt) => apt.id);

        // Query for intake form status for all appointments
        const { data, error } = await supabase
          .from("intake_forms") // Adjust table name as needed
          .select("appointment_id, status, submitted_at")
          .in("appointment_id", appointmentIds);

        if (error) {
          console.error("Error fetching intake status:", error);
          return;
        }

        // Update intake status state
        const statusMap = {};
        data?.forEach((intake) => {
          statusMap[intake.appointment_id] = {
            status: intake.status,
            submitted_at: intake.submitted_at,
          };
        });

        setIntakeStatus(statusMap);
      } catch (error) {
        console.error("Error in checkIntakeStatus:", error);
      }
    };

    // Initial check
    checkIntakeStatus();

    // Set up polling interval (adjust frequency as needed)
    const interval = setInterval(checkIntakeStatus, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [appointments]);

  useEffect(() => {
    let timer;
    if (joinModalOpen && joinCountdown > 0) {
      timer = setInterval(() => {
        setJoinCountdown((prev) => (prev > 1000 ? prev - 1000 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [joinModalOpen, joinCountdown]);

  // Function to manually trigger intake form creation
  const createIntakeForm = async (appointmentId) => {
    try {
      // Call your backend API or Supabase function to create intake form
      const { data, error } = await supabase.rpc("create_intake_form", {
        p_appointment_id: appointmentId,
      });

      if (error) {
        console.error("Error creating intake form:", error);
        return;
      }

      // Update local state
      setIntakeStatus((prev) => ({
        ...prev,
        [appointmentId]: {
          status: "created",
          submitted_at: null,
        },
      }));

      console.log("Intake form created successfully");
    } catch (error) {
      console.error("Error creating intake form:", error);
    }
  };



  const currentAppointment =
    appointments.find((apt) => apt.id === selectedAppointmentId) || appointment;

  const currentIntakeStatus = intakeStatus[selectedAppointmentId];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const opts = { weekday: "short", month: "short", day: "numeric" };
    return d.toLocaleDateString("en-US", opts);
  };

  const formatTime = (timeString) => {
    return timeString || "";
  };

  const lawyer = useSelector((s) => s.attorneyData.attorneyData);
  const videoToken = useSelector((s) => s.attorneyData.videoToken);

  const handleJoin = (appt) => {
    const startISO = appt.requestedTimestamp || appt.appointment_time;
    const endISO = appt.end_ts;
    const { allowed, countdown, start, end } = canJoinMeetingNow(startISO, endISO);
    if (!allowed) {
      const meetingDetails = formatMeetingDetails(start, end);
      setJoinWindowText(meetingDetails.fullText);
      setJoinCountdown(countdown > 0 ? countdown : 0);
      setJoinModalOpen(true);
      return;
    }
    const url = new URL("/lawyer-video", window.location.origin);
    url.searchParams.append("token", JSON.stringify(videoToken));
    url.searchParams.append("userId", JSON.stringify(lawyer.user_id));
    url.searchParams.append("callId", JSON.stringify(appt.id));
    url.searchParams.append("name", JSON.stringify(lawyer.name));
    window.open(url, "_blank");
  };

  // Function to render intake status
  const renderIntakeStatus = () => {
    if (currentIntakeStatus?.status === "submitted") {
      return (
        <div className="adv-alert adv-alert-success">
          <Warning className="adv-alert-icon" />
          <span>
            Intake form submitted on{" "}
            {new Date(currentIntakeStatus.submitted_at).toLocaleDateString()}
          </span>
        </div>
      );
    } else if (currentIntakeStatus?.status === "created") {
      return (
        <div className="adv-alert adv-alert-info">
          <Warning className="adv-alert-icon" />
          <span>
            Intake form created and sent to client. Waiting for submission.
          </span>
        </div>
      );
    } else {
      return (
        <div className="adv-alert adv-alert-warning">
          <Warning className="adv-alert-icon" />
          <span>Accept the appointment to create and send an intake form.</span>
        </div>
      );
    }
  };

  return (
    <div className="adv-container">
      {/* Left Sidebar */}
      <aside className="adv-sidebar">
        <div className="adv-sidebar-header">
          <button className="adv-back-button" onClick={onBack}>
            <ArrowBack className="adv-back-icon" />
            <span className="adv-back-text">Back to Requests</span>
          </button>
        </div>
        <div className="adv-card-list">
          {appointments.map((apt) => {
            const isSelected = apt.id === selectedAppointmentId;
            const aptIntakeStatus = intakeStatus[apt.id];

            return (
              <div
                key={apt.id}
                className={`adv-card ${isSelected ? "selected" : ""}`}
                onClick={() => setSelectedAppointmentId(apt.id)}
              >
                <div className="adv-card-content">
                  <div className="adv-card-avatar">
                    {apt.client?.first_name?.[0]}
                    {apt.client?.last_name?.[0]}
                  </div>
                  <div className="adv-card-text">
                    <div className="adv-card-name">
                      {apt.client?.first_name} {apt.client?.last_name}
                    </div>
                    <div className="adv-card-sub">
                      <span className="adv-card-type">
                        {apt.appointmentType?.appointment_name ||
                          "No appointment name"}
                      </span>
                      <span className="adv-card-datetime">
                        {formatDate(apt.requested_slot?.date)}{" "}
                        {formatTime(apt.requested_slot?.time)}
                      </span>
                    </div>
                    {/* Show intake status indicator */}
                    {aptIntakeStatus?.status === "submitted" && (
                      <div className="adv-intake-indicator submitted">
                        ✓ Intake Submitted
                      </div>
                    )}
                    {aptIntakeStatus?.status === "created" && (
                      <div className="adv-intake-indicator pending">
                        ⏳ Intake Pending
                      </div>
                    )}
                  </div>
                  <div className="adv-card-docs">
                    <Description className="adv-doc-icon" />
                    <span className="adv-doc-count">
                      {apt.documents?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main Content */}
      <main className="adv-main">
        <div className="adv-main-content">
          {/* Header */}
          <div className="adv-header">
            <div className="adv-header-left">
              <div className="adv-title">
                {currentAppointment.appointmentType?.appointment_name ||
                  "No appointment name"}
              </div>
              <div className="adv-subheader">
                <div className="adv-subheader-left">
                  <div className="adv-sub-avatar">
                    {currentAppointment.client?.first_name?.[0]}
                    {currentAppointment.client?.last_name?.[0]}
                  </div>
                  <div className="adv-client-details">
                    <div className="adv-client-name">
                      {currentAppointment.client?.first_name}{" "}
                      {currentAppointment.client?.last_name}
                    </div>
                    <div className="adv-client-category">
                      {currentAppointment.appointmentType?.category ||
                        "Consultation"}
                    </div>
                  </div>
                </div>
                <div className="adv-subheader-right">
                  {formatDate(currentAppointment.requested_slot?.date)} at{" "}
                  {formatTime(currentAppointment.requested_slot?.time)}
                </div>
              </div>
              <div className="adv-amount">
                Total Amount: $
                {currentAppointment.appointmentType?.price || 120}
              </div>
            </div>
            <div className="adv-header-actions">
              <button
                className="adv-action-button primary"
                onClick={() => handleJoin(currentAppointment)}
              >
                Join Meeting
              </button>
              <button
                className="adv-action-button primary"
                onClick={() => openChat(currentAppointment.client)}
              >
                Chat{" "}
              </button>
              <button
                className="adv-action-button outlined"
                // onClick={() => onReject(currentAppointment.id)}
              >
                Reschedule
              </button>
              <button className="adv-action-button outlined">Transfer</button>
            </div>
          </div>
          {joinModalOpen && (
            <div className="adv-section" style={{ 
              maxWidth: 500, 
              margin: '24px auto', 
              textAlign: 'center', 
              borderRadius: '16px', 
              border: '1px solid #e3e8ff', 
              background: 'white',
              boxShadow: '0 4px 20px rgba(20, 74, 140, 0.15)',
              fontFamily: 'Poppins, sans-serif',
              overflow: 'hidden'
            }}>
              <div style={{ 
                backgroundColor: '#144A8C', 
                color: 'white', 
                padding: '20px',
                fontWeight: 600,
                fontSize: '18px'
              }}>
                ⏰ Meeting Not Yet Available
              </div>
              <div style={{ padding: '32px 24px 24px 24px' }}>
                <div style={{ 
                  fontSize: '16px', 
                  color: '#666', 
                  marginBottom: '16px',
                  fontWeight: 500
                }}>
                  This meeting is scheduled for:
                </div>
                <div style={{ 
                  color: '#144A8C', 
                  fontWeight: 600, 
                  fontSize: '16px', 
                  marginBottom: '24px',
                  lineHeight: '1.4',
                  backgroundColor: '#f8f9ff',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid #e3e8ff'
                }}>
                  {joinWindowText}
                </div>
                {joinCountdown > 0 ? (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    gap: '12px',
                    backgroundColor: '#fff3e0',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #ffe0b2'
                  }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#f57c00', 
                      fontWeight: 500
                    }}>
                      You can join in:
                    </div>
                    <div style={{ 
                      color: '#f57c00', 
                      fontWeight: 700, 
                      fontSize: '28px',
                      fontFamily: 'monospace',
                      letterSpacing: '2px'
                    }}>
                      {formatCountdown(joinCountdown)}
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    backgroundColor: '#ffebee',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid #ffcdd2'
                  }}>
                    <div style={{ 
                      color: '#d32f2f', 
                      fontWeight: 500,
                      textAlign: 'center'
                    }}>
                      The meeting is not currently accessible.
                    </div>
                  </div>
                )}
                <button 
                  className="adv-action-button outlined" 
                  onClick={() => setJoinModalOpen(false)} 
                  style={{ 
                    marginTop: '24px',
                    backgroundColor: '#144A8C',
                    color: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 500,
                    borderRadius: '8px',
                    padding: '10px 32px',
                    border: 'none',
                    fontSize: '16px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#0d3a6b'
                    }
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Client Info Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Client Info</h2>
            <div className="adv-info-grid">
              <div className="adv-info-item">
                <Person className="adv-icon" />
                <span className="adv-info-label">Full legal name</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.first_name}{" "}
                  {currentAppointment.client?.last_name}
                </span>
              </div>
              <div className="adv-info-item">
                <CalendarToday className="adv-icon" />
                <span className="adv-info-label">Date of birth</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.dob || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <Person className="adv-icon" />
                <span className="adv-info-label">Gender</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.gender || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Email</span>
                <span className="adv-info-value-inline">
                  {currentAppointment.client?.email || "Not provided"}
                  {currentAppointment.client?.email && (
                    <button className="adv-copy-button">
                      <ContentCopy className="adv-copy-icon" />
                    </button>
                  )}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Address</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.address || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Phone</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.phone || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item adv-message-item">
                <span className="adv-info-label adv-no-icon">Message</span>
                <div className="adv-message-box">
                  {currentAppointment.message || "No message provided"}
                </div>
              </div>
            </div>
          </section>

          {/* Documents Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Documents</h2>
            {currentAppointment.documents?.length > 0 ? (
              currentAppointment.documents.map((doc, idx) => (
                <div key={idx} className="adv-doc-item">
                  <div className="adv-doc-info">
                    <Description className="adv-doc-svg" />
                    <div className="adv-doc-texts">
                      <div className="adv-doc-name">
                        {doc.name || doc.filename || `Document_${idx + 1}.pdf`}
                      </div>
                      <div className="adv-doc-size">
                        {doc.size || "Unknown size"}
                      </div>
                    </div>
                  </div>
                  <button className="adv-download-button">
                    <Download className="adv-download-icon" />
                  </button>
                </div>
              ))
            ) : (
              <div className="adv-no-docs">No documents uploaded</div>
            )}
          </section>

          {/* Intake Form Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Intake Form</h2>
            {renderIntakeStatus()}

          </section>

          {/* AI Summary Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">AI Summary</h2>
            <div className="adv-alert adv-alert-info">
              <Warning className="adv-alert-icon" />
              <div className="adv-alert-text">
                <div className="adv-alert-title">
                  AI suggests:{" "}
                  <strong>
                    {currentAppointment.ai_recommendation || "Accept"}
                  </strong>
                </div>
                <div className="adv-alert-body">
                  {currentAppointment.ai_reason ||
                    "No AI analysis available for this appointment."}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <ChatModal open={chatOpen} onClose={closeChat} clientUser={chatUser} />
    </div>
  );
};

export default AppointmentDetailView;
